import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(() => !!localStorage.getItem("tk_original_token"));

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      localStorage.removeItem("tk_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("tk_token", data.token);
      setUser(data);
      setImpersonating(false);
      localStorage.removeItem("tk_original_token");
      return { success: true, data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || "Errore login" };
    }
  };

  const loginSso = async (ssoToken) => {
    try {
      const { data } = await api.post("/auth/sso/totalklick/callback", { sso_token: ssoToken });
      localStorage.setItem("tk_token", data.token);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || "Verifica SSO fallita" };
    }
  };

  const register = async (email, password, name, network_id) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, network_id });
      localStorage.setItem("tk_token", data.token);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || "Errore registrazione" };
    }
  };

  const registerCustomer = async (payload) => {
    try {
      const { data } = await api.post("/auth/register-customer", payload);
      localStorage.setItem("tk_token", data.token);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || "Errore registrazione" };
    }
  };

  const impersonate = async (userId) => {
    try {
      const currentToken = localStorage.getItem("tk_token");
      const { data } = await api.post("/superadmin/impersonate", { user_id: userId });
      if (currentToken) localStorage.setItem("tk_original_token", currentToken);
      localStorage.setItem("tk_token", data.token);
      setImpersonating(true);
      await fetchMe();
      return { success: true, data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || "Errore impersonation" };
    }
  };

  const stopImpersonating = async () => {
    const original = localStorage.getItem("tk_original_token");
    if (original) {
      localStorage.setItem("tk_token", original);
      localStorage.removeItem("tk_original_token");
    }
    setImpersonating(false);
    await fetchMe();
    window.location.href = "/superadmin";
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("tk_token");
    localStorage.removeItem("tk_original_token");
    setImpersonating(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, impersonating,
      login, loginSso, register, registerCustomer,
      impersonate, stopImpersonating,
      logout, refresh: fetchMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
